/**
 * Provider shell for the CoordioCortex web app: error boundary, router, MSAL auth, React Query,
 * SignalR and the Vuexy-style conditional layout. The route table itself lives in
 * `src/routes/CortexAppRoutes.tsx` so that path constants, rendered pages and redirect stubs stay
 * readable independently of the provider nesting.
 */

import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toast, ErrorBoundary } from './components/common';
import { ConditionalLayout } from './components/layout/ConditionalLayout';
import { CortexAppRoutes } from './routes/CortexAppRoutes';

import { settingsService } from './services/settingsService';
import { useAuthStore } from './store/authStore';
import { useSettingsStore } from './store/settingsStore';
import { useContextStore } from './store/contextStore';
import { SignalRProvider } from './providers/SignalRProvider';
import { MsalProviderWithAuth } from './providers/MsalProviderWithAuth';

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Main App component
 */
function App() {
  const { isAuthenticated, token } = useAuthStore();
  const { updateSettings } = useSettingsStore();

  const loadModules = useContextStore((s) => s.loadModules);
  const evaluateGlobalAccess = useContextStore((s) => s.evaluateGlobalAccess);

  useEffect(() => {
    if (import.meta.env.DEV) console.log('[App] auth state', { isAuthenticated });
  }, [isAuthenticated]);

  // Load enabled modules from API so the menu and route guard can filter. Re-run on auth change:
  // global-access role names are only returned to authenticated callers (SEC-L1), and loadModules()
  // re-evaluates global access internally once they load.
  useEffect(() => {
    loadModules();
  }, [loadModules, isAuthenticated]);

  useEffect(() => {
    evaluateGlobalAccess(token);
  }, [token, evaluateGlobalAccess]);

  // Best-effort: load persisted user settings and apply theme.
  // This keeps the UI in sync with backend preferences after login.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;
    settingsService
      .getMyUserSettings()
      .then((s) => {
        if (cancelled) return;
        if (s.theme === 'light' || s.theme === 'dark' || s.theme === 'system') {
          updateSettings({ mode: s.theme });
        }
      })
      .catch(() => {
        // Ignore errors here (backend may be unavailable in dev); user settings page can retry.
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, updateSettings]);

  return (
    <ErrorBoundary>
      {/*
        No `future` prop: `v7_startTransition` and `v7_relativeSplatPath` were opt-in flags on
        react-router-dom 6 and are the default behaviour on 7, where the prop no longer exists.
        Both were already enabled here, so the upgrade to 7 changed no routing behaviour.
      */}
      <BrowserRouter>
        <MsalProviderWithAuth>
          <QueryClientProvider client={queryClient}>
            <SignalRProvider>
            <ConditionalLayout>
              <CortexAppRoutes />
              <Toast />
            </ConditionalLayout>
        </SignalRProvider>
      </QueryClientProvider>
        </MsalProviderWithAuth>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
